/**
 * Unified actions for AI status updates, toast notifications, and debug logging.
 * This file provides a consistent way to handle AI status updates with side effects.
 */

import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AiLog, AiPredictionLogsWrite } from '@timelyapp/integration-cloud-api-spec';
import type { CreateToastOptions } from 'redux/modules/toasts.types';
import type { IState } from 'redux/state';
import debug from 'utils/debug';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import { getCurrentAccountId } from '~redux/selectors/accounts';
import { getUserId } from '~redux/selectors/user';

// Lazy import to break circular dependency: predictions.sideEffects.actions ↔ predictions.actions
const getInjectAiLogToStatusForDateRange = () =>
  import('./predictions.actions').then(m => m.injectAiLogToStatusForDateRange);

/**
 * Options for setting AI status with side effects
 */
export type SetAiStatusOptions = Pick<AiPredictionLogsWrite, 'event' | 'day' | 'message'> & {
  /** When to set the event timestamp (defaults to now) */
  updated_at?: string;
  /** Additional data to include in debug logs */
  debugData?: Record<string, any>;
  /**
   * By default, status will be saved to server.
   * @default true
   */
  saveToServer?: boolean;
  /** Toast notification options (if omitted, no toast is shown) */
  toast?: Omit<CreateToastOptions, 'message'> & {
    /** Toast message (defaults to AI log message if not provided) */
    message?: string;
  };
};

/**
 * Sets AI status (ai_prediction_logs), stored log to server, shows toast notification, and logs debug message in one call.
 */
export const setAiStatus = createAsyncThunk<
  void,
  SetAiStatusOptions,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/setAiStatus', async (options, { dispatch, getState }) => {
  const state = getState();
  // const isViewingTimesheet = getIsViewingTimesheet(state);
  const { event, day, saveToServer = true, debugData = {} } = options;
  const updated_at = options.updated_at || new Date().toISOString();
  const message = options.message ?? `AI ${event.replace('_', ' ')}`;
  const toast = options.toast;

  // Optimistically update ASAP.
  const aiLog: AiLog = { day, event, updated_at, message };
  const injectAiLogToStatusForDateRange = await getInjectAiLogToStatusForDateRange();
  dispatch(injectAiLogToStatusForDateRange(aiLog));
  debug.template.ai(`AI Status set to "${event}"`, { aiLog, toast: toast || 'none', ...debugData });

  // Certain events cause issues, and don't matter enough to add complexity to deriveAiStatus.
  // (e.g, _pending -> time delay -> _requested gets stuck in pending if reloading before debounce/throttle hits)
  // So just throw, so we can't miss it.
  const blockServerSaveForEvents = ['training_pending', 'inference_pending'];

  if (saveToServer) {
    if (blockServerSaveForEvents.includes(event)) {
      // If you're here to fix, make sure deriveAiStatus supports "stuck in _pending" events (+ apiSlice)
      throw new Error(`AI Status "${event}" is not allowed to be saved to server.`);
    }

    const timely_account_id = getCurrentAccountId(state);
    const timely_user_id = getUserId(state);
    const body = { ...aiLog, timely_user_id, timely_account_id, updated_at };
    dispatch(ticApi.endpoints.setAiPredictionStatus.initiate({ body }));
  } else {
    debug.template.ai(`${event} not synced to API`, { aiLog, ...debugData });
  }

  // Don't show AI-related toasts if viewing timesheet
  // if (toast && !isViewingTimesheet) {
  // dispatch(showToast({ ...toast, message: toast.message || message }));
  // }
});
